/**
 * STRYDE — composants des sections "sx-*"
 * Vanilla JS, sans dépendance. Chaque composant :
 *  - est initialisé par section (plusieurs instances possibles),
 *  - est détruit / réinitialisé sur les événements de l'éditeur de thème Shopify.
 * Le panier s'appuie sur le product-form natif d'Horizon (aucun ajout simulé).
 */
(() => {
  'use strict';

  if (window.Stryde && window.Stryde.__loaded) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const designMode = () => Boolean(window.Shopify && window.Shopify.designMode);
  const registry = new WeakMap(); // element -> instance

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const smooth = () => (reduceMotion.matches ? 'auto' : 'smooth');

  /* ------------------------------------------------------------------ */
  /* Money                                                               */
  /* ------------------------------------------------------------------ */
  function formatMoney(cents, format) {
    const fmt = format || '{{amount_with_comma_separator}} €';
    const value = Number(cents) || 0;
    const delimit = (num, precision, thousands, decimal) => {
      const fixed = (num / 100).toFixed(precision);
      const [int, dec] = fixed.split('.');
      const intPart = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dec ? intPart + decimal + dec : intPart;
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount':
          return delimit(value, 2, ',', '.');
        case 'amount_no_decimals':
          return delimit(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return delimit(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return delimit(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return delimit(value, 2, "'", '.');
        case 'amount_with_space_separator':
          return delimit(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return delimit(value, 0, ' ', ',');
        default:
          return delimit(value, 2, '.', ',');
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Reveal au scroll                                                    */
  /* ------------------------------------------------------------------ */
  let revealObserver = null;
  function getRevealObserver() {
    if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;
    revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
    return revealObserver;
  }

  function initReveal(root) {
    const items = $$('[data-sx-reveal]', root);
    if (!items.length) return;
    const io = getRevealObserver();
    const skipAnimation = reduceMotion.matches || designMode() || !io;
    const vh = window.innerHeight || 800;
    items.forEach((el) => {
      if (el.dataset.sxRevealInit) return;
      el.dataset.sxRevealInit = '1';
      if (skipAnimation) {
        el.classList.add('is-in');
        return;
      }
      const rect = el.getBoundingClientRect();
      // Seuls les éléments sous la ligne de flottaison sont masqués : pas de flash, pas de contenu perdu si le JS échoue.
      if (rect.top > vh * 0.9) {
        el.classList.add('sx-pre');
        if (el.dataset.sxReveal === 'clip') el.classList.add('sx-pre--clip');
      } else {
        el.classList.add('is-in');
        return;
      }
      io.observe(el);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Carousel (scroll-snap natif + flèches + points + autoplay)          */
  /* ------------------------------------------------------------------ */
  class Carousel {
    constructor(root) {
      this.root = root;
      this.track = $('[data-sx-track]', root);
      this.prev = $('[data-sx-prev]', root);
      this.next = $('[data-sx-next]', root);
      this.dotsEl = $('[data-sx-dots]', root);
      this.autoplayMs = Number(root.dataset.autoplay) || 0;
      this.paused = false;
      this.visible = false;
      this.index = 0;
      this.pageCount = 1;
      this.abort = new AbortController();
      if (!this.track) return;

      const { signal } = this.abort;
      this.onScroll = this.onScroll.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true, signal });
      this.prev?.addEventListener('click', () => this.go(this.index - 1), { signal });
      this.next?.addEventListener('click', () => this.go(this.index + 1), { signal });
      this.track.addEventListener(
        'keydown',
        (e) => {
          if (e.key === 'ArrowRight') {
            e.preventDefault();
            this.go(this.index + 1);
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            this.go(this.index - 1);
          }
        },
        { signal }
      );

      // Pause autoplay sur interaction
      const pause = () => (this.paused = true);
      const resume = () => (this.paused = false);
      root.addEventListener('pointerenter', pause, { signal });
      root.addEventListener('pointerleave', resume, { signal });
      root.addEventListener('focusin', pause, { signal });
      root.addEventListener('focusout', resume, { signal });
      this.track.addEventListener('touchstart', () => (this.userTouched = true), { passive: true, signal });

      this.ro = new ResizeObserver(() => this.measure());
      this.ro.observe(this.track);

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          this.visible = entries[0].isIntersecting;
        });
        this.io.observe(root);
      }

      if (this.autoplayMs > 0 && !reduceMotion.matches) {
        this.timer = setInterval(() => this.tick(), this.autoplayMs);
      }
      this.measure();
    }

    get items() {
      return Array.from(this.track.children).filter((el) => el.offsetParent !== null || el.getClientRects().length);
    }

    get step() {
      const items = this.items;
      if (items.length < 2) return items[0]?.getBoundingClientRect().width || this.track.clientWidth;
      return items[1].offsetLeft - items[0].offsetLeft;
    }

    get scrollable() {
      return this.track.scrollWidth - this.track.clientWidth > 4;
    }

    measure() {
      const items = this.items;
      if (!items.length) return;
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const step = this.step || 1;
      this.pageCount = this.scrollable ? Math.min(items.length, Math.round(maxScroll / step) + 1) : 1;
      this.root.toggleAttribute('data-static', !this.scrollable);
      this.renderDots();
      this.onScroll();
    }

    renderDots() {
      if (!this.dotsEl) return;
      if (this.dotsEl.childElementCount === this.pageCount) return;
      this.dotsEl.innerHTML = '';
      if (this.pageCount < 2) return;
      const label = this.dotsEl.dataset.label || 'Aller à l’élément';
      for (let i = 0; i < this.pageCount; i++) {
        const li = document.createElement('li');
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'sx-dot';
        b.setAttribute('aria-label', `${label} ${i + 1}`);
        b.addEventListener('click', () => this.go(i));
        li.appendChild(b);
        this.dotsEl.appendChild(li);
      }
    }

    onScroll() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        const step = this.step || 1;
        const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        let idx = Math.round(this.track.scrollLeft / step);
        if (this.track.scrollLeft >= maxScroll - 4) idx = this.pageCount - 1;
        this.index = Math.max(0, Math.min(this.pageCount - 1, idx));
        if (this.dotsEl) {
          Array.from(this.dotsEl.querySelectorAll('button')).forEach((b, i) => {
            b.setAttribute('aria-current', String(i === this.index));
          });
        }
        if (this.prev) this.prev.disabled = this.index <= 0;
        if (this.next) this.next.disabled = this.index >= this.pageCount - 1;
      });
    }

    go(i, behavior = smooth()) {
      const items = this.items;
      if (!items.length) return;
      const target = Math.max(0, Math.min(this.pageCount - 1, i));
      const first = items[0];
      const left = items[target].offsetLeft - first.offsetLeft;
      this.track.scrollTo({ left, behavior });
    }

    goToElement(el) {
      const idx = this.items.indexOf(el);
      if (idx > -1) this.go(Math.min(idx, this.pageCount - 1));
    }

    tick() {
      if (this.paused || this.userTouched || !this.visible || document.hidden || !this.scrollable || designMode()) return;
      this.go(this.index >= this.pageCount - 1 ? 0 : this.index + 1);
    }

    destroy() {
      this.abort.abort();
      this.ro?.disconnect();
      this.io?.disconnect();
      clearInterval(this.timer);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Accordéons (details/summary) animés                                  */
  /* ------------------------------------------------------------------ */
  class Accordion {
    constructor(details) {
      this.el = details;
      this.summary = $('summary', details);
      this.panel = $('.sx-acc__panel', details) || details.lastElementChild;
      this.abort = new AbortController();
      this.summary?.addEventListener('click', (e) => this.onClick(e), { signal: this.abort.signal });
    }

    onClick(e) {
      if (reduceMotion.matches || !this.panel || !this.panel.animate) {
        if (!this.el.open) this.closeSiblings();
        return;
      }
      e.preventDefault();
      if (this.anim) this.anim.cancel();
      if (this.el.open) this.close();
      else this.open();
    }

    closeSiblings() {
      const group = this.el.closest('[data-sx-acc-group]');
      if (!group) return;
      $$('details[open]', group).forEach((d) => {
        if (d === this.el) return;
        const inst = registry.get(d);
        if (inst) inst.close();
        else d.removeAttribute('open');
      });
    }

    open() {
      this.closeSiblings();
      this.el.open = true;
      const h = this.panel.scrollHeight;
      this.anim = this.panel.animate(
        [
          { height: '0px', opacity: 0 },
          { height: `${h}px`, opacity: 1 },
        ],
        { duration: 380, easing: 'cubic-bezier(.22,1,.36,1)' }
      );
      this.anim.onfinish = () => (this.anim = null);
    }

    close() {
      if (!this.el.open) return;
      const h = this.panel.scrollHeight;
      this.anim = this.panel.animate(
        [
          { height: `${h}px`, opacity: 1 },
          { height: '0px', opacity: 0 },
        ],
        { duration: 300, easing: 'cubic-bezier(.22,1,.36,1)' }
      );
      this.anim.onfinish = () => {
        this.el.open = false;
        this.anim = null;
      };
    }

    destroy() {
      this.abort.abort();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Étapes interactives                                                  */
  /* ------------------------------------------------------------------ */
  class Steps {
    constructor(root) {
      this.root = root;
      this.steps = $$('[data-sx-step]', root);
      this.images = $$('[data-sx-step-img]', root);
      this.progress = $('.sx-step__progress', root);
      this.tag = $('[data-sx-step-tag]', root);
      this.abort = new AbortController();
      this.current = 0;
      const { signal } = this.abort;
      this.steps.forEach((step, i) => {
        step.addEventListener('click', () => this.select(i, true), { signal });
        step.addEventListener(
          'keydown',
          (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              this.select(i, true);
            } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
              e.preventDefault();
              this.steps[Math.min(i + 1, this.steps.length - 1)].focus();
            } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
              e.preventDefault();
              this.steps[Math.max(i - 1, 0)].focus();
            }
          },
          { signal }
        );
      });
      this.select(0);

      const ms = Number(root.dataset.autoplay) || 0;
      if (ms && !reduceMotion.matches && 'IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => (this.visible = entries[0].isIntersecting), { threshold: 0.4 });
        this.io.observe(root);
        this.timer = setInterval(() => {
          if (!this.visible || this.userPicked || document.hidden || designMode()) return;
          this.select((this.current + 1) % this.steps.length);
        }, ms);
      }
    }

    select(i, byUser = false) {
      if (byUser) this.userPicked = true;
      this.current = i;
      this.steps.forEach((s, k) => {
        s.classList.toggle('is-active', k === i);
        s.setAttribute('aria-selected', String(k === i));
        s.tabIndex = k === i ? 0 : -1;
      });
      this.images.forEach((img) => img.classList.toggle('is-active', Number(img.dataset.sxStepImg) === i));
      if (this.tag) this.tag.textContent = this.steps[i]?.dataset.label || '';
      if (this.progress && this.steps.length > 1) {
        const first = this.steps[0].getBoundingClientRect();
        const cur = this.steps[i].getBoundingClientRect();
        this.progress.style.height = `${Math.max(0, cur.top - first.top)}px`;
      }
    }

    destroy() {
      this.abort.abort();
      this.io?.disconnect();
      clearInterval(this.timer);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Galerie produit + zoom + lightbox                                    */
  /* ------------------------------------------------------------------ */
  class Gallery {
    constructor(root) {
      this.root = root;
      this.track = $('[data-sx-gallery-track]', root);
      this.slides = $$('[data-sx-slide]', root);
      this.thumbs = $$('[data-sx-thumb]', root);
      this.counter = $('[data-sx-counter]', root);
      this.dots = $('[data-sx-gallery-dots]', root);
      this.dialog = $('[data-sx-lightbox]', root);
      this.index = 0;
      this.abort = new AbortController();
      if (!this.track || !this.slides.length) return;
      const { signal } = this.abort;

      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
      $('[data-sx-gallery-prev]', root)?.addEventListener('click', () => this.go(this.index - 1), { signal });
      $('[data-sx-gallery-next]', root)?.addEventListener('click', () => this.go(this.index + 1), { signal });
      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.go(i), { signal }));
      this.track.addEventListener(
        'keydown',
        (e) => {
          if (e.key === 'ArrowRight') {
            e.preventDefault();
            this.go(this.index + 1);
          }
          if (e.key === 'ArrowLeft') {
            e.preventDefault();
            this.go(this.index - 1);
          }
        },
        { signal }
      );

      if (this.dots && this.slides.length > 1) {
        this.slides.forEach((_, i) => {
          const li = document.createElement('li');
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'sx-dot';
          b.setAttribute('aria-label', `${this.dots.dataset.label || 'Image'} ${i + 1}`);
          b.addEventListener('click', () => this.go(i), { signal });
          li.appendChild(b);
          this.dots.appendChild(li);
        });
      }

      // Zoom au survol (souris uniquement) + ouverture lightbox au clic
      const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
      $$('[data-sx-zoom]', root).forEach((btn, i) => {
        const slideIndex = btn.dataset.index !== undefined ? Number(btn.dataset.index) : i;
        btn.addEventListener('click', () => this.openLightbox(slideIndex), { signal });
        if (!fine.matches) return;
        btn.addEventListener('pointerenter', () => !reduceMotion.matches && btn.classList.add('is-zooming'), { signal });
        btn.addEventListener('pointerleave', () => btn.classList.remove('is-zooming'), { signal });
        btn.addEventListener(
          'pointermove',
          (e) => {
            const r = btn.getBoundingClientRect();
            btn.style.setProperty('--zx', `${((e.clientX - r.left) / r.width) * 100}%`);
            btn.style.setProperty('--zy', `${((e.clientY - r.top) / r.height) * 100}%`);
          },
          { signal }
        );
      });

      if (this.dialog) {
        this.lbImg = $('[data-sx-lightbox-img]', this.dialog);
        $('[data-sx-lightbox-close]', this.dialog)?.addEventListener('click', () => this.dialog.close(), { signal });
        $('[data-sx-lightbox-prev]', this.dialog)?.addEventListener('click', () => this.showLightbox(this.lbIndex - 1), { signal });
        $('[data-sx-lightbox-next]', this.dialog)?.addEventListener('click', () => this.showLightbox(this.lbIndex + 1), { signal });
        this.dialog.addEventListener(
          'click',
          (e) => {
            if (e.target === this.dialog) this.dialog.close();
          },
          { signal }
        );
        this.dialog.addEventListener(
          'keydown',
          (e) => {
            if (e.key === 'ArrowRight') this.showLightbox(this.lbIndex + 1);
            if (e.key === 'ArrowLeft') this.showLightbox(this.lbIndex - 1);
          },
          { signal }
        );
        let sx = null;
        this.dialog.addEventListener('touchstart', (e) => (sx = e.touches.length === 1 ? e.touches[0].clientX : null), { passive: true, signal });
        this.dialog.addEventListener(
          'touchend',
          (e) => {
            if (sx === null) return;
            const dx = e.changedTouches[0].clientX - sx;
            sx = null;
            if (Math.abs(dx) > 50) this.showLightbox(this.lbIndex + (dx < 0 ? 1 : -1));
          },
          { passive: true, signal }
        );
        this.dialog.addEventListener('close', () => this.go(this.lbIndex, 'auto'), { signal });
      }

      document.addEventListener('sx:variant-change', (e) => {
        if (e.detail?.sectionId !== root.dataset.sectionId) return;
        const mediaId = e.detail.variant?.media_id;
        if (!mediaId) return;
        const idx = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
        if (idx > -1) this.go(idx);
      }, { signal });

      this.update();
    }

    onScroll() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        const w = this.track.clientWidth || 1;
        const idx = Math.round(this.track.scrollLeft / w);
        if (idx !== this.index) {
          this.index = Math.max(0, Math.min(this.slides.length - 1, idx));
          this.update();
        }
      });
    }

    go(i, behavior = smooth()) {
      const n = this.slides.length;
      const idx = ((i % n) + n) % n;
      this.index = idx;
      this.track.scrollTo({ left: this.slides[idx].offsetLeft - this.slides[0].offsetLeft, behavior });
      this.update();
    }

    update() {
      const n = this.slides.length;
      if (this.counter) this.counter.textContent = `${this.index + 1} / ${n}`;
      this.thumbs.forEach((t, i) => t.setAttribute('aria-current', String(i === this.index)));
      if (this.dots) $$('button', this.dots).forEach((b, i) => b.setAttribute('aria-current', String(i === this.index)));
      this.slides.forEach((s, i) => s.setAttribute('aria-hidden', String(i !== this.index)));
      $$('[data-sx-zoom]', this.root).forEach((b, i) => (b.tabIndex = i === this.index ? 0 : -1));
    }

    openLightbox(i) {
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.showLightbox(i);
      this.dialog.showModal();
    }

    showLightbox(i) {
      const n = this.slides.length;
      const dir = i < (this.lbIndex ?? i) ? -1 : 1;
      let idx = ((i % n) + n) % n;
      // ignore les médias non-image (vidéo, 3D)
      for (let k = 0; k < n && !this.slides[idx].dataset.full; k++) idx = (((idx + dir) % n) + n) % n;
      this.lbIndex = idx;
      const slide = this.slides[this.lbIndex];
      if (this.lbImg && slide) {
        this.lbImg.src = slide.dataset.full;
        this.lbImg.alt = slide.dataset.alt || '';
      }
    }

    destroy() {
      this.abort.abort();
      if (this.dialog?.open) this.dialog.close();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Produit : offres, quantité, prix, barre sticky, retour panier        */
  /* ------------------------------------------------------------------ */
  class ProductForm {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.form = $('form[data-sx-main-form]', root);
      this.idInput = this.form ? $('input[name="id"]', this.form) : null;
      this.qtyInput = $('[data-sx-qty-input]', root);
      this.atc = $('[data-sx-main-atc]', root);
      this.sticky = $('[data-sx-sticky]', root);
      this.moneyFormat = root.dataset.moneyFormat;
      this.abort = new AbortController();
      const { signal } = this.abort;
      try {
        this.variants = JSON.parse($('[data-sx-variants]', root)?.textContent || '[]');
      } catch (err) {
        this.variants = [];
      }
      if (!this.form || !this.idInput) return;

      $$('[data-sx-pack]', root).forEach((radio) => {
        radio.addEventListener('change', () => this.setVariant(radio.value, true), { signal });
      });

      // Quantité
      $('[data-sx-qty-minus]', root)?.addEventListener('click', () => this.setQty(this.qty - 1), { signal });
      $('[data-sx-qty-plus]', root)?.addEventListener('click', () => this.setQty(this.qty + 1), { signal });
      this.qtyInput?.addEventListener('change', () => this.setQty(this.qty), { signal });

      // Retour visuel ajout panier (événements standard émis par Horizon)
      this.form.addEventListener('submit', () => this.setLoading(true), { signal });
      root.addEventListener('shopify:cart:lines-update', (e) => this.onCartEvent(e), { signal });
      root.addEventListener('shopify:cart:error', () => this.setLoading(false), { signal });

      // CTA externes (barre sticky, bloc final)
      document.addEventListener(
        'click',
        (e) => {
          const trigger = e.target.closest?.('[data-sx-submit-main]');
          if (!trigger) return;
          e.preventDefault();
          this.submit();
        },
        { signal }
      );

      this.setupSticky();
      const current = this.variants.find((v) => String(v.id) === String(this.idInput.value));
      this.setVariant(current ? current.id : this.variants[0]?.id, false);
    }

    get qty() {
      const v = parseInt(this.qtyInput?.value || '1', 10);
      return Number.isFinite(v) && v > 0 ? v : 1;
    }

    get variant() {
      return this.variants.find((v) => String(v.id) === String(this.idInput.value));
    }

    setQty(n) {
      if (!this.qtyInput) return;
      const max = this.variant?.max || 99;
      const val = Math.max(1, Math.min(max, Number(n) || 1));
      this.qtyInput.value = String(val);
      const minus = $('[data-sx-qty-minus]', this.root);
      const plus = $('[data-sx-qty-plus]', this.root);
      if (minus) minus.disabled = val <= 1;
      if (plus) plus.disabled = val >= max;
      this.renderPrices();
    }

    setVariant(id, byUser) {
      const v = this.variants.find((x) => String(x.id) === String(id));
      if (!v) return;
      this.idInput.value = String(v.id);
      // L'input caché est une "ref" Horizon : on notifie les écouteurs éventuels.
      this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      const radio = $(`[data-sx-pack][value="${v.id}"]`, this.root);
      if (radio && !radio.checked) radio.checked = true;
      this.setQty(this.qty);
      this.renderAvailability(v);
      if (byUser) {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          window.history.replaceState({}, '', url.toString());
        } catch (e) {
          /* noop */
        }
      }
      document.dispatchEvent(new CustomEvent('sx:variant-change', { detail: { sectionId: this.sectionId, variant: v } }));
    }

    renderPrices() {
      const v = this.variant;
      if (!v) return;
      const qty = this.qty;
      const price = formatMoney(v.price * qty, this.moneyFormat);
      const compare = v.compare_at_price > v.price ? formatMoney(v.compare_at_price * qty, this.moneyFormat) : '';
      const save = v.compare_at_price > v.price ? formatMoney((v.compare_at_price - v.price) * qty, this.moneyFormat) : '';
      const pct = v.compare_at_price > v.price ? Math.round(((v.compare_at_price - v.price) / v.compare_at_price) * 100) : 0;
      $$('[data-sx-price]').forEach((el) => (el.textContent = el.dataset.sxPrice === 'unit' ? formatMoney(v.price, this.moneyFormat) : price));
      $$('[data-sx-compare]').forEach((el) => {
        el.textContent = el.dataset.sxCompare === 'unit' ? (compare ? formatMoney(v.compare_at_price, this.moneyFormat) : '') : compare;
        el.hidden = !compare;
      });
      $$('[data-sx-save]').forEach((el) => {
        const tpl = el.dataset.template || 'Économisez [amount]';
        el.textContent = tpl.replace('[amount]', save).replace('[percent]', `${pct}`);
        el.hidden = !save;
      });
      $$('[data-sx-variant-title]').forEach((el) => (el.textContent = v.title));
    }

    renderAvailability(v) {
      const btns = [this.atc, ...$$('[data-sx-submit-main]')].filter(Boolean);
      btns.forEach((b) => {
        const label = $('[data-sx-atc-label]', b);
        if (v.available) {
          b.disabled = false;
          b.removeAttribute('aria-disabled');
          if (label) label.textContent = label.dataset.available || label.textContent;
        } else {
          if (b.tagName === 'BUTTON') b.disabled = true;
          else b.setAttribute('aria-disabled', 'true');
          if (label) label.textContent = label.dataset.soldout || 'Épuisé';
        }
      });
      $$('[data-sx-stock]', this.root).forEach((el) => {
        el.dataset.available = String(v.available);
        el.textContent = v.available ? el.dataset.in : el.dataset.out;
      });
    }

    submit() {
      if (!this.form) return;
      if (this.atc?.disabled) return;
      if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(this.atc || undefined);
      else this.atc?.click();
    }

    setLoading(on) {
      [this.atc, ...$$('[data-sx-submit-main]')].forEach((b) => b && b.classList.toggle('is-loading', on));
    }

    onCartEvent(e) {
      const done = (ok) => {
        this.setLoading(false);
        if (!ok || !this.atc) return;
        [this.atc, ...$$('[data-sx-submit-main]')].forEach((b) => b && b.classList.add('is-added'));
        clearTimeout(this.addedTimer);
        this.addedTimer = setTimeout(() => {
          [this.atc, ...$$('[data-sx-submit-main]')].forEach((b) => b && b.classList.remove('is-added'));
        }, 2200);
        this.hideSticky();
      };
      if (e.promise && typeof e.promise.then === 'function') {
        e.promise.then((r) => done(!r?.detail?.didError)).catch(() => done(false));
      } else {
        done(true);
      }
    }

    /* Barre sticky : visible une fois le CTA principal dépassé, masquée si CTA visible / panier ouvert / checkout. */
    setupSticky() {
      if (!this.sticky || !this.atc || !('IntersectionObserver' in window)) return;
      const { signal } = this.abort;
      this.state = { atcVisible: true, pastAtc: false, finalVisible: false, cartOpen: false, checkout: false };
      this.stickyIo = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.atc) {
            this.state.atcVisible = entry.isIntersecting;
            this.state.pastAtc = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          } else {
            this.state.finalVisible = entry.isIntersecting;
          }
        });
        this.renderSticky();
      });
      this.stickyIo.observe(this.atc);
      $$('[data-sx-final-cta]').forEach((el) => this.stickyIo.observe(el));

      const drawer = document.getElementById('cart-drawer');
      if (drawer) {
        this.drawerMo = new MutationObserver(() => {
          this.state.cartOpen = drawer.hasAttribute('open');
          this.renderSticky();
        });
        this.drawerMo.observe(drawer, { attributes: true, attributeFilter: ['open'] });
      }
      document.addEventListener(
        'click',
        (e) => {
          if (e.target.closest?.('[name="checkout"], a[href*="/checkout"], .shopify-payment-button')) {
            this.state.checkout = true;
            this.renderSticky();
          }
        },
        { signal }
      );
      window.addEventListener(
        'pageshow',
        () => {
          this.state.checkout = false;
          this.renderSticky();
        },
        { signal }
      );
    }

    hideSticky() {
      /* rien de plus : l'ouverture du tiroir panier masque la barre via l'observateur */
    }

    renderSticky() {
      if (!this.sticky || !this.state) return;
      const s = this.state;
      const show = s.pastAtc && !s.atcVisible && !s.finalVisible && !s.cartOpen && !s.checkout;
      this.sticky.classList.toggle('is-visible', show);
      this.sticky.setAttribute('aria-hidden', String(!show));
      $$('button, a', this.sticky).forEach((el) => (el.tabIndex = show ? 0 : -1));
    }

    destroy() {
      this.abort.abort();
      this.stickyIo?.disconnect();
      this.drawerMo?.disconnect();
      clearTimeout(this.addedTimer);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Initialisation / cycle de vie éditeur                                */
  /* ------------------------------------------------------------------ */
  const components = [
    ['[data-sx-carousel]', Carousel],
    ['details.sx-acc', Accordion],
    ['[data-sx-steps]', Steps],
    ['[data-sx-gallery]', Gallery],
    ['[data-sx-product]', ProductForm],
  ];

  /* Footer : accordéons fermés sur mobile, ouverts à partir de 750px */
  const footerMq = window.matchMedia('(min-width: 750px)');
  function syncFooter(root = document) {
    $$('[data-sx-footer-col]', root).forEach((d) => {
      d.open = footerMq.matches;
    });
  }
  footerMq.addEventListener?.('change', () => syncFooter());

  function init(root = document) {
    initReveal(root);
    syncFooter(root);
    components.forEach(([selector, Ctor]) => {
      const nodes = root.matches?.(selector) ? [root, ...$$(selector, root)] : $$(selector, root);
      nodes.forEach((el) => {
        if (registry.has(el)) return;
        try {
          registry.set(el, new Ctor(el));
        } catch (err) {
          console.error('[Stryde]', selector, err);
        }
      });
    });
  }

  function destroy(root) {
    components.forEach(([selector]) => {
      const nodes = root.matches?.(selector) ? [root, ...$$(selector, root)] : $$(selector, root);
      nodes.forEach((el) => {
        registry.get(el)?.destroy?.();
        registry.delete(el);
      });
    });
  }

  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => destroy(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const block = e.target;
    const carouselEl = block.closest?.('[data-sx-carousel]');
    if (carouselEl) {
      const inst = registry.get(carouselEl);
      if (inst) {
        const item = Array.from(inst.track.children).find((c) => c === block || c.contains(block));
        if (item) inst.goToElement(item);
      }
    }
    const details = block.matches?.('details') ? block : block.querySelector?.('details.sx-acc');
    if (details && !details.open) details.open = true;
    const step = block.closest?.('[data-sx-step]');
    if (step) {
      const steps = registry.get(step.closest('[data-sx-steps]'));
      steps?.select(steps.steps.indexOf(step));
    }
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  window.Stryde = { __loaded: true, init, destroy, formatMoney };
})();
